import { useEffect, useState } from 'react'
import { Button, StatusBar } from '../../design'
import { APP_BUILD_ID } from './buildId'
import { UPDATE_COPY } from './copy'
import { useSwUpdate } from './registerSw'
import { isReloadRecent, recordReload, sessionMarkerStorage } from './reloadMarker'
import { useDeployedUpdate } from './useDeployedUpdate'

const WAITING_WORKER_BUILD_ID = 'waiting-worker'

export const RELOAD_FALLBACK_MS = 3_000

type UpdatePromptProps = {
  buildId?: string | null
  enabled?: boolean
  fetchImpl?: typeof fetch
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  reload?: () => void
  controlled?: () => boolean
}

const reloadWindow = () => window.location.reload()

const pageIsControlled = () => navigator.serviceWorker?.controller != null

export const UpdatePrompt = ({
  buildId = APP_BUILD_ID,
  enabled = import.meta.env.PROD,
  fetchImpl,
  storage,
  reload = reloadWindow,
  controlled = pageIsControlled,
}: UpdatePromptProps) => {
  const [preparing, setPreparing] = useState(false)
  const markerStorage = storage ?? sessionMarkerStorage()
  const deployedBuildId = useDeployedUpdate({ buildId, enabled, ...(fetchImpl === undefined ? {} : { fetchImpl }) })
  const { waiting, check, activate } = useSwUpdate()

  useEffect(() => {
    if (deployedBuildId !== null) check()
  }, [deployedBuildId, check])

  const pendingBuildId = deployedBuildId ?? (waiting ? WAITING_WORKER_BUILD_ID : null)
  if (pendingBuildId === null || isReloadRecent(markerStorage, pendingBuildId, new Date())) return null

  const apply = async () => {
    if (await activate()) {
      recordReload(markerStorage, pendingBuildId, new Date())
      setTimeout(reload, RELOAD_FALLBACK_MS)
      return
    }
    if (controlled()) {
      setPreparing(true)
      check()
      return
    }
    recordReload(markerStorage, pendingBuildId, new Date())
    reload()
  }

  return (
    <div className="status-bar">
      <StatusBar
        tone="action"
        title={UPDATE_COPY.title}
        detail={UPDATE_COPY.detail}
        action={
          <Button
            variant="primary"
            pending={preparing && !waiting}
            pendingLabel={UPDATE_COPY.preparing}
            onClick={() => void apply()}
          >
            {UPDATE_COPY.reload}
          </Button>
        }
      />
    </div>
  )
}
